import { useOwnerDashboard, useBusinessUnits } from "@/hooks/useOwnerDashboard";
import { RevenueCard, CollectionsCard, OutstandingArCard, GrossMarginCard, AttentionStrip } from "@/components/dashboard/FinancialCards";
import { CaseLifecycleCard, RiskSlaCard, RenewalsHorizonCard, SalesFunnelCard, ActivityStreamCard, TeamWorkloadCard, WhereWorkTimeIsTrappedCard, FirstPassYieldByAuthorityCard, CaseRiskRegisterCard, CashPositionCard } from "@/components/dashboard/PanelCards";
import HrPayrollControlCard from "@/components/dashboard/HrPayrollControlCard";
import { SurveillanceSummaryCard } from "@/components/dashboard/SurveillanceSummaryCard";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { PlusCircle, FileDown, Settings2 } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { exportDashboardToPdf } from "@/lib/exportDashboard";
import { useAuth } from "@/context/AuthContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { hasHrAccess } from "@/lib/workspaceAccess";

const PERIODS = [
  { code: "TODAY", label: "Today" },
  { code: "7D", label: "7D" },
  { code: "MTD", label: "MTD" },
  { code: "QTD", label: "QTD" },
  { code: "YTD", label: "YTD" },
];

// 2026-07-20 · Dual-workspace opt-in toggle key.
// The HR Payroll control card is no longer auto-injected into the
// Certification Owner Dashboard whenever the tenant carries the HR
// bundle. Users with `hasHrAccess(user)` see a compact "Customize"
// toggle in the filter bar that flips the HR card on/off; the choice
// is persisted per-browser and defaults to OFF.
const HR_CARD_TOGGLE_KEY = "certopz.dashboard.hrCard.enabled";

function _readHrCardPref() {
  try { return window.localStorage.getItem(HR_CARD_TOGGLE_KEY) === "1"; }
  catch (_err) { return false; }
}

function _writeHrCardPref(enabled) {
  try { window.localStorage.setItem(HR_CARD_TOGGLE_KEY, enabled ? "1" : "0"); }
  catch (_err) { /* noop */ }
}

export default function Dashboard() {
  const { data, isLoading, error, filters, setFilters, refetch } = useOwnerDashboard();
  const businessUnits = useBusinessUnits();
  // 2026-02-28 · Filter bar now surfaces **Industry** (captured on
  // the customer master — e.g. Pharmaceuticals, Food, Electronics)
  // instead of the earlier Service-Category "Domain" filter. Pulls
  // from the tenant `industries` master so the options mirror what
  // is actually assignable on Customers.
  const [industries, setIndustries] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { getIndustries } = await import("@/lib/masterCache");
        const rows = await getIndustries();
        if (!alive) return;
        setIndustries(Array.isArray(rows) ? rows : []);
      } catch (_) { /* non-fatal */ }
    })();
    return () => { alive = false; };
  }, []);
  const { user } = useAuth();
  const sub = useSubscription();
  const exportRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  // RBAC: hide if explicit deny; otherwise allow (Owner Dashboard is owner-scoped anyway)
  const canExport = !user || !user.permissions || user.permissions.includes("reports.export") || user.permissions.includes("dashboard.export") || (user.roles || []).some(r => ["Owner", "Admin", "Finance Manager"].includes(r));

  // Phase HR-DASH-ENTITLEMENT (2026-07-08) — entitlement gate.
  // 2026-07-20 · Auto-injection removed. The wrapper is now visible
  // ONLY when BOTH (a) the tenant has the HR module entitlement AND
  // (b) THIS user carries hasHrAccess() — i.e. the widget mount is
  // gated on the same access predicate the HR workspace uses. The
  // final visibility is still user-controlled via the opt-in toggle
  // (default OFF).
  const hrEntitlementReady = sub && !sub.loading && sub.hasModule("hr") && hasHrAccess(user);
  const [hrCardEnabled, setHrCardEnabled] = useState(_readHrCardPref);
  useEffect(() => { _writeHrCardPref(hrCardEnabled); }, [hrCardEnabled]);
  const hrWrapperVisible = hrEntitlementReady && hrCardEnabled;

  if (isLoading && !data) return <DashboardSkeleton />;
  if (error && !data) return <DashboardError onRetry={refetch} />;
  if (!data) return null;

  const c = data.context || {};
  const today = new Date(c.refreshedAt || Date.now());
  const todayLabel = today.toLocaleDateString("en-US", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  const attentionCount = (data.attention || []).reduce((s, x) => s + (x.value > 0 ? 1 : 0), 0);

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const meta = {
        title: "CertOpz · Owner Dashboard",
        tenant: c.tenantName || "",
        period: c.period || filters.period || "MTD",
        financialYearId: filters.financialYearId,
        businessUnitId: filters.businessUnitId || "all",
        businessUnitLabel: (businessUnits.find(u => u.id === filters.businessUnitId)?.name) || "All Business Units",
        domainId: filters.industryId || "all",
        domainLabel: (industries.find(d => d.id === filters.industryId)?.name) || "All Industries",
        generatedAt: new Date(),
      };
      await exportDashboardToPdf(exportRef.current, meta);
      toast.success("Dashboard PDF exported.");
    } catch (e) {
      console.error("Export failed", e);
      toast.error("Unable to export PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="dashboard-screen" className="owner-dashboard-page pb-8">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4" data-testid="dashboard-filterbar">        <div className="flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="period-tabs">
          {PERIODS.map((p) => (
            <button key={p.code} onClick={() => setFilters({ ...filters, period: p.code })}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition ${c.period === p.code ? "bg-slate-900 text-white shadow" : "text-slate-600 hover:text-slate-900"}`}
              data-testid={`period-${p.code.toLowerCase()}`}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Industry</span>
          <select
            value={filters.industryId || "all"}
            onChange={(e) => setFilters({ ...filters, industryId: e.target.value })}
            className="text-[10px] uppercase tracking-widest text-slate-500 font-bold bg-white border border-slate-200 rounded-lg px-2 py-1.5 w-44"
            data-testid="industry-select"
          >
            <option value="all">All Industries</option>
            {industries.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>
        {hrEntitlementReady ? (
          <label
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 hover:text-slate-900 cursor-pointer select-none"
            data-testid="dashboard-hr-card-toggle-label"
          >
            <Settings2 className="w-3.5 h-3.5 text-slate-400" />
            <span className="uppercase tracking-wider">Customize</span>
            <span className="text-slate-400 mx-1">·</span>
            <input
              type="checkbox"
              checked={hrCardEnabled}
              onChange={(e) => setHrCardEnabled(e.target.checked)}
              className="accent-teal-600"
              data-testid="dashboard-hr-card-toggle"
            />
            <span>Show HR Payroll card</span>
          </label>
        ) : null}
      </div>

      {/* Heading */}
      <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="dashboard-title text-3xl lg:text-4xl" style={{ fontFamily: "Plus Jakarta Sans" }}>Dashboard</h1>
          <div className="text-sm text-slate-500 mt-1" data-testid="dashboard-subtitle">
            {todayLabel} · {attentionCount > 0 ? `${attentionCount} thing${attentionCount > 1 ? "s" : ""} need you today` : "All clear today"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canExport ? (
            <Button variant="outline" size="sm" data-testid="export-pdf-btn" onClick={handleExport} disabled={exporting}>
              <FileDown className="w-4 h-4 mr-1" /> {exporting ? "Exporting…" : "Export PDF"}
            </Button>
          ) : null}
          <Link to="/cases"><Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="new-case-btn"><PlusCircle className="w-4 h-4 mr-1" /> New Case</Button></Link>
        </div>
      </div>

      <div id="owner-dashboard-export-root" ref={exportRef}>
        {/* Financial KPIs — each card guards `!data` internally so an
            older or partial backend payload renders a skeleton row
            instead of throwing. */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <RevenueCard data={data.financialKpis?.revenue} period={c.period} />
          <CollectionsCard data={data.financialKpis?.collections} period={c.period} />
          <OutstandingArCard data={data.financialKpis?.outstandingAr} />
          <GrossMarginCard data={data.financialKpis?.grossMargin} />
        </div>

        <AttentionStrip items={data.attention} />

        {/* PILOT-POLISH-1 — HR Payroll Control tile moved up from the
            bottom of the dashboard so Owner sees payroll-state counts
            within the first-screen scan, alongside the operational
            attention strip. Compact tile · hidden when the user lacks
            `hr.dashboard.read` · counts-only, no salary amounts.

            2026-02-27 · Phase Owner-Dash-HR-Width — wrapper changed from
            a `lg:grid-cols-3` row (which left the card as a narrow 1-of-3
            column with empty space on the right) to a full-width slot.
            The card now spans the same horizontal envelope as the
            CaseLifecycleCard / RiskSlaCard row below it. Internal
            section layout is responsive inside the card itself.
        */}
        {hrWrapperVisible ? (
          <div className="mb-6">
            <HrPayrollControlCard />
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="md:col-span-2"><CaseLifecycleCard data={data.caseLifecycle} /></div>
          <RiskSlaCard data={data.riskSla} />
        </div>

        {/* 2026-09-06 · Sprint DASH-CMD-CENTRE — panels 4 + 5 side by side.
            2026-09-05 · Responsive audit — bumped the breakpoint from
            `lg:grid-cols-2` to `md:grid-cols-2` so tablets no longer
            render a wasted single-column stack. Both panels are dense
            lists that render cleanly ≥ ~360 px column width. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6"
             data-testid="dashboard-slice2-row">
          <WhereWorkTimeIsTrappedCard data={data.riskSla?.stageBottlenecks} />
          <FirstPassYieldByAuthorityCard
            data={data.caseLifecycle?.firstPassYieldByAuthority}
            legacyOverall={data.caseLifecycle?.firstPassApprovalPercent}
          />
        </div>

        {/* 2026-09-06 · Sprint DASH-CMD-CENTRE Slice 4 · Panel 3 —
            13-week cash position. Rendered full-width above the risk
            register because runway is the single-most-consequential
            KPI on this dashboard and its caveats must be reachable
            without a scroll. */}
        {data.cashPosition ? (
          <div className="mb-6" data-testid="dashboard-slice4-row">
            <CashPositionCard data={data.cashPosition} />
          </div>
        ) : null}

        {/* 2026-09-06 · Sprint DASH-CMD-CENTRE — panel 6 case risk register.
            Full-width table because comparison across rows is the point.
            Renders nothing when the tenant has no at-risk cases. */}
        {data.riskRegister ? (
          <div className="mb-6" data-testid="dashboard-slice3-row">
            <CaseRiskRegisterCard data={data.riskRegister} />
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="md:col-span-2"><RenewalsHorizonCard data={data.renewals} /></div>
          <SalesFunnelCard data={data.salesFunnel} />
        </div>

        <div className="mb-6" data-testid="dashboard-surveillance-row">
          <SurveillanceSummaryCard />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2"><ActivityStreamCard items={data.activityStream} /></div>
          <TeamWorkloadCard items={data.teamWorkload} stages={data.teamWorkloadStages} recommendations={data.recommendations} />
        </div>
      </div>

      <div className="mt-6 text-center text-[11px] text-slate-400">
        CertOpz · Owner Dashboard v2 · Tenant-scoped to {c.tenantName} · Refreshed {new Date(c.refreshedAt).toLocaleTimeString()}
      </div>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4" data-testid="dashboard-skeleton">
      <div className="h-12 bg-slate-100 rounded animate-pulse" />
      <div className="grid grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}
      </div>
      <div className="h-24 bg-slate-100 rounded-xl animate-pulse" />
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-64 bg-slate-100 rounded-xl animate-pulse" />)}
      </div>
    </div>
  );
}

function DashboardError({ onRetry }) {
  return (
    <div className="co-card p-10 text-center" data-testid="dashboard-error">
      <div className="text-lg font-bold text-slate-900">Could not load dashboard</div>
      <div className="text-sm text-slate-500 mt-2">Please retry or check your connection.</div>
      <Button className="mt-4" onClick={onRetry} data-testid="dashboard-retry">Retry</Button>
    </div>
  );
}
